import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import styled from 'styled-components';

import ResultObject from 'dashboard/components/WorkflowControlPanel/ResultObject';
import StepConfigAction from 'dashboard/components/WorkflowControlPanel/StepConfigAction';
import StepConfigConditional from 'dashboard/components/WorkflowControlPanel/StepConfigConditional';
import StepConfigLoop from 'dashboard/components/WorkflowControlPanel/StepConfigLoop';
import StepConfigMapOutput from 'dashboard/components/WorkflowControlPanel/StepConfigLoop/StepConfigMapOutput';
import StepConfigModule from 'dashboard/components/WorkflowControlPanel/StepConfigModule';
import StepConfigParallel from 'dashboard/components/WorkflowControlPanel/StepConfigParallel';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import StepConfigWait from 'dashboard/components/WorkflowControlPanel/StepConfigWait';
import WorkflowStepError from 'dashboard/components/WorkflowControlPanel/WorkflowStepError';
import WorkflowRunStepStats from 'dashboard/components-lib/WorkflowRunStepStats';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { ProductFeatureName } from 'dashboard/models/organization';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import {
  WorkflowRunStep,
  WorkflowRunStepAction,
  WorkflowRunStepConditional,
  WorkflowRunStepKnownObjects,
  WorkflowRunStepLoop,
  WorkflowRunStepStatus,
} from 'dashboard/models/workflow-step';
import {
  ActualWorkflowStep,
  FakeWorkflowStepType,
  WorkflowStep,
  WorkflowStepInput,
  WorkflowStepLoop,
  WorkflowStepMapOutput,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Stack, { HStack } from 'design-system/components/Stack';
import { headingMixins } from 'design-system/mixins/text';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';
import { TokensToNode } from 'lib/workflows';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

const Card = styled.div`
  display: flex;
  width: 100%;
  border: 2px solid ${Color.CoreSlate50};
  border-radius: ${Spacing[2]};
  padding: 14px;
  background-color: ${Color.PrimaryWhite};
  align-items: center;
`;

const CardTitle = styled.div`
  ${Typography.TextMediumBold}
`;

const StyledStack = styled(Stack)`
  width: 100%;
`;

interface StepConfigProps {
  usableObjects: UsableObjectConfig[];
  inputStep: WorkflowStepInput;
  workflowSchema: WorkflowSchema;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  editable: boolean;
  errors: WorkflowError[];
  stepsConfig: WorkflowStep[];
  tokensToSteps: TokensToNode;
  activeStep: ActualWorkflowStep | WorkflowStepMapOutput;
  conditionalRouteToEdit: number | null;
  setConditionalRouteToEdit: Dispatch<SetStateAction<number>>;
  handleClose: () => void;
  updateStep: (newStep: WorkflowStep, skipContext?: boolean, template?: TemplateFilter) => void;
  deleteStep: (stepToDelete: WorkflowStep) => void;
  focusStep: (token: string) => void;
  workflowRun: WorkflowRun;
  workflowRunStep?: WorkflowRunStep;
  knownObjects?: WorkflowRunStepKnownObjects[];
  isLoadingLoopCycle?: boolean;
  updateCycleIndex?: (idx: number, token: string) => void;
  loopTokensToCycleIdx?: Record<string, number>;
  showRunStats: boolean;
}

const StepConfig: FC<StepConfigProps & PropsFromRedux> = ({
  inputStep,
  workflowSchema,
  platformObjectJsonSchema,
  editable,
  errors,
  stepsConfig,
  tokensToSteps,
  activeStep,
  conditionalRouteToEdit,
  setConditionalRouteToEdit,
  handleClose,
  deleteStep,
  updateStep,
  focusStep,
  workflowRunStep = null,
  knownObjects = null,
  workflowSteps,
  workflowRun,
  isLoadingLoopCycle = false,
  updateCycleIndex,
  loopTokensToCycleIdx,
  showRunStats,
  currentEnvironment,
  usableObjects,
  ...props
}) => {
  const hasWorkflowsEnhanced = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.WorkflowsEnhanced,
    currentEnvironment.type,
  );
  const hasWorkflowsFree = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.WorkflowsFree,
    currentEnvironment.type,
  );
  const hasWorkflowsBasic = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.WorkflowsBasic,
    currentEnvironment.type,
  );

  const knownObjectToken = workflowRunStep?.attributes?.['known-objects-map']?.[activeStep.token];
  const object = knownObjects?.find((knownObject) => knownObject.id === knownObjectToken);
  const { trigger } = inputStep;

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const usableObjectsSingular = useMemo(
    () => usableObjects.filter(({ isArray }) => !isArray),
    [usableObjects],
  );

  const {
    register,
    handleSubmit,
    setValue: setName,
  } = useForm<ActualWorkflowStep | WorkflowStepMapOutput>();

  const editor = () => {
    setName('name', activeStep.name);
    switch (activeStep.type) {
      case WorkflowStepType.Action:
        return (
          <StepConfigAction
            editable={editable}
            errors={errors}
            organization={props.organization}
            step={activeStep}
            stepsConfig={stepsConfig}
            inputStep={inputStep}
            usableObjects={usableObjectsSingular}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            workflowRunStep={workflowRunStep as WorkflowRunStepAction}
            updateStep={updateStep}
            focusStep={focusStep}
            workflowSchema={workflowSchema}
            hasWorkflowsEnhanced={hasWorkflowsEnhanced}
            hasWorkflowsFree={hasWorkflowsFree}
            hasWorkflowsBasic={hasWorkflowsBasic}
          />
        );
      case WorkflowStepType.Parallel:
        return (
          <StepConfigParallel
            step={activeStep}
            editable={editable}
            errors={errors}
            updateStep={updateStep}
          />
        );
      case WorkflowStepType.Wait:
        return (
          <StepConfigWait
            hasWorkflowsEnhanced={hasWorkflowsEnhanced}
            editable={editable}
            errors={errors}
            focusStep={focusStep}
            step={activeStep}
            key={activeStep.token}
            usableObjects={usableObjects}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            workflowSchema={workflowSchema}
            stepsConfig={stepsConfig}
            updateStep={updateStep}
            inputStep={inputStep}
          />
        );
      case WorkflowStepType.Conditional:
        return (
          <StepConfigConditional
            trigger={trigger}
            step={activeStep}
            workflowSchema={workflowSchema}
            usableObjects={usableObjects}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            editable={editable}
            errors={errors}
            stepsConfig={stepsConfig}
            conditionalRouteToEdit={conditionalRouteToEdit}
            workflowRunStep={workflowRunStep as WorkflowRunStepConditional}
            updateStep={updateStep}
            setConditionalRouteToEdit={setConditionalRouteToEdit}
          />
        );
      case WorkflowStepType.Loop:
        return (
          <StepConfigLoop
            trigger={trigger}
            step={activeStep}
            workflowSchema={workflowSchema}
            usableObjects={usableObjects}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            editable={editable}
            errors={errors}
            stepsConfig={stepsConfig}
            updateStep={updateStep}
            workflowRunStep={workflowRunStep as WorkflowRunStepLoop}
            updateCycleIndex={updateCycleIndex}
            cycleIdx={loopTokensToCycleIdx?.[activeStep.token]}
            isLoadingCycle={isLoadingLoopCycle}
          />
        );
      case FakeWorkflowStepType.MapOutput: {
        const loopStep = tokensToSteps[activeStep.token].parent.step as WorkflowStepLoop;
        return (
          <StepConfigMapOutput
            usableObjects={usableObjects}
            loopStep={loopStep}
            editable={editable}
            errors={errors}
            updateStep={updateStep}
          />
        );
      }
      case WorkflowStepType.Module:
        return (
          <StepConfigModule
            step={activeStep}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            trigger={trigger}
            editable={editable}
            errors={errors}
            updateStep={updateStep}
          />
        );
    }
  };

  const handleChangeNodeName = (newStep: ActualWorkflowStep | WorkflowStepMapOutput) => {
    if (activeStep.type === FakeWorkflowStepType.MapOutput) {
      return;
    }
    updateStep({
      ...activeStep,
      name: newStep.name,
    });
  };

  return (
    <>
      <Container data-test='workflow-step-control-panel__control-panel-container'>
        {workflowRunStep?.attributes?.['step-error'] &&
          (workflowRunStep?.attributes?.['status'] === WorkflowRunStepStatus.Errored ||
            workflowRunStep.attributes['step-error']['continued-after-error']) && (
            <div data-test='workflow-step-control-panel__error-container'>
              <WorkflowStepError
                error={workflowRunStep.attributes['step-error']}
                tokensToDisplayValue={tokensToDisplayValue}
              />
              <Spacer height={2} />
            </div>
          )}
        <SubTitle>Step Type</SubTitle>
        {editable && activeStep.type !== FakeWorkflowStepType.MapOutput ? (
          <form>
            <Input
              {...register('name')}
              aria-label='Step name'
              onBlur={handleSubmit(handleChangeNodeName)}
            />
          </form>
        ) : (
          activeStep.name
        )}
        <Spacer height={2} />
        <StepConfigTypeCard type={activeStep.type} />
        {showRunStats && workflowRunStep && (
          <>
            <Spacer height={1} />
            <Card>
              <StyledStack>
                <CardTitle>Stats</CardTitle>
                <WorkflowRunStepStats step={workflowRunStep} showHelpText={true} />
              </StyledStack>
            </Card>
          </>
        )}
        <Spacer height={2} />
        <SubTitle>Configuration</SubTitle>
        {editor()}
      </Container>
      <Spacer height={3} />
      {workflowRunStep && object && (
        <>
          <SubTitle>Details</SubTitle>
          <ResultObject object={object} />
          <Spacer height={3} />
        </>
      )}
      <HStack equal>
        {editable && activeStep.type !== FakeWorkflowStepType.MapOutput && (
          <Button
            type='button'
            id='workflow-step-control-panel__button_delete'
            onClick={() => deleteStep(activeStep)}
          >
            Delete
          </Button>
        )}

        <Button type='button' id='workflow-step-control-panel__button-close' onClick={handleClose}>
          Close
        </Button>
      </HStack>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: state.organization.organization,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  workflowSteps: state.workflowRun.steps,
  workflowRun: state.workflowRun.workflowRun,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export default connector(StepConfig);
